import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Table from '../ogComponent/table';
import { Link } from 'react-router-dom';
import { IconCheck, IconX } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import Spinner from '../ogComponent/spinner';
import debounce from 'lodash/debounce';

export default function RequestList() {
    const [selectedRequest, setSelectedRequest] = useState("Pending Requests");
    const [product, setProduct] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const accessToken = localStorage.getItem('accessToken');

    const fetchData = async (search = '') => {
        setLoading(true);
        try {
            // Map dropdown text to API status parameter
            const statusMap = {
                "Pending Requests": "Pending",
                "Approved Requests": "Approved",
                "Rejected Requests": "Rejected"
            };

            const status = statusMap[selectedRequest];

            const response = await axios.get(`${process.env.REACT_APP_API}/api/attendance/attendance-regularization/`, {
                params: { 
                    status, 
                    search: search.trim() 
                },
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.data.success) {
                const formattedData = response.data.data.map((item, index) => ({
                    ...item,
                    SiNo: index + 1,
                    name: item.employee?.name || "N/A",
                    date: item.date,
                    reason: item.reason,
                    punch_type: item.punch_direction === 'CHECK_IN' ? 'Punch-In' : 'Punch-Out',
                    punch_time: item.punch_time,
                    status: item.status,
                    approved: (
                        <button
                            className="btn btn-outline-success d-flex align-items-center"
                            style={{ height: '25px' }}
                            onClick={() => handleApproveReject(item.id, 'Approved')}
                        >
                            <IconCheck stroke={2} />
                        </button>
                    ),
                    cancel: (
                        <button
                            className="btn btn-outline-danger d-flex align-items-center"
                            style={{ height: '25px' }}
                            onClick={() => handleApproveReject(item.id, 'Rejected')}
                        >
                            <IconX stroke={2} />
                        </button>
                    )
                }));
                setProduct(formattedData);
            } else {
                setProduct([]);
                setError('Failed to fetch data');
            }
        } catch (error) {
            setProduct([]);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Create a debounced version of fetchData
    const debouncedFetchData = useCallback(
        debounce((search) => {
            fetchData(search);
        }, 1000),
        [selectedRequest, accessToken]
    );

    useEffect(() => {
        debouncedFetchData(searchQuery);
        
        // Cleanup the debounce on component unmount
        return () => {
            debouncedFetchData.cancel();
        };
    }, [searchQuery, selectedRequest, accessToken]);

    const handleApproveReject = async (requestId, status) => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API}/api/attendance/attendance-regularization/${requestId}/approve-reject/`,
                { status },
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                // Show success toast
                toast.success(`Request ${status.toLowerCase()}`);

                // Refresh the data after successful approval/rejection
                fetchData(searchQuery);
            } else {
                // Show error toast
                toast.error('Failed to process the request');
            }
        } catch (error) {
            // Show error toast
            toast.error(error.response?.data?.message || 'An error occurred');
            console.error('Error processing request:', error);
        }
    };

    const handleSelect = (request) => {
        setSelectedRequest(request);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const header = [
        { name: "SiNo", label: "#", active: true },
        { name: "name", label: "Name", active: true },
        { name: "reason", label: "Reason", active: true },
        { name: "date", label: "Date", active: true },
        { name: "punch_type", label: "Punch Type", active: true },
        { name: "punch_time", label: "Punch Time", active: true },
        {
            name: "cancel",
            label: "",
            active: selectedRequest === "Pending Requests" || selectedRequest === "Approved Requests",
        },
        {
            name: "approved",
            label: "",
            active: selectedRequest === "Pending Requests" || selectedRequest === "Rejected Requests",
        },
    ];

    return (
        <div className='px-4 py-3 w-100'>
            <h4>Punch Requests</h4>

            <div className='d-flex justify-content-between align-items-center'>
                <div className="dropdown d-flex align-items-center gap-2 gap-md-3 py-2">
                    <button
                        className="btn dropdown-toggle text-white border-0"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                            fontSize: "clamp(12px, 1.4vw, 16px)",
                            backgroundColor: "#ba6f01",
                            maxHeight: "40px",
                            minWidth: "200px",
                        }}
                    >
                        {selectedRequest}
                    </button>
                    <ul className="dropdown-menu">
                        <li onClick={() => handleSelect("Pending Requests")}>
                            <Link className="dropdown-item">Pending Requests</Link>
                        </li>
                        <li onClick={() => handleSelect("Approved Requests")}>
                            <Link className="dropdown-item">Approved Requests</Link>
                        </li>
                        <li onClick={() => handleSelect("Rejected Requests")}>
                            <Link className="dropdown-item">Rejected Requests</Link>
                        </li>
                    </ul>
                </div>

                <input 
                    className="form-control w-50" 
                    type="search" 
                    placeholder="Search..." 
                    aria-label="Search" 
                    style={{height:'35px'}}
                    value={searchQuery}
                    onChange={handleSearchChange}
                />
            </div>

            {/* Conditionally render either the table or the "No Attendance" message */}
            {loading ? (
                <Spinner />
            ) : product.length === 0 ? (
                <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                        backgroundImage: 'url(/assets/file.png)',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: 0.3,
                    }}></div>
                    <p className="m-0 fw-light fs-5 position-relative">No Requests !</p>
                </div>
            ) : (
                <Table header={header} product={product} onRowClick={() => { }} />
            )}
        </div>
    );
}